import { Card, Space, Tag } from "antd";
import { List } from "./ui/List";
import { getDayStatus } from "@/utils/attendance";
import { IAttendance } from "@/types/attendance-sheet.types";
import { useSearchParams } from "react-router-dom";
import { useUrlParams } from "@/hooks/useUrlParams";
import dayjs from "dayjs";
import { Checkmark, X } from "./ui/Icons";
import { startCase } from "lodash-es";

export default function AttendanceSummary({
  attendanceSheet,
}: {
  attendanceSheet: IAttendance;
}) {
  // Search params
  const [searchParams] = useSearchParams();
  const { isProviderView, userType } = useUrlParams({
    searchParams,
  });

  // Attendance sheet status
  const _status = getDayStatus(attendanceSheet, {
    userType,
  });

  console.log("attendanceSheet", attendanceSheet);

  return (
    <>
      <Card title="Summary" className="my-10">
        <div className="flex justify-start gap-4 lg:gap-32 flex-col lg:flex-row mb-12 lg:mb-0 mt-2">
          <List
            items={[
              {
                title: "Overview",
                items: [
                  {
                    label: "Period",
                    value: `${dayjs(attendanceSheet?.periodStartDate).format(
                      "MMM DD, YYYY"
                    )} - ${dayjs(attendanceSheet?.periodEndDate).format(
                      "MMM DD, YYYY"
                    )}`,
                  },
                  {
                    label: "Status",
                    value: _status ? (
                      <Tag className="m-0" color={_status.color}>
                        {_status.title}
                      </Tag>
                    ) : (
                      <Tag className="m-0">
                        {startCase(
                          attendanceSheet?.status?.split("_").join(" ")
                        )}
                      </Tag>
                    ),
                  },
                ],
              },
            ]}
          />

          {attendanceSheet?.submittedViaPaper !== true && (
            <List
              items={[
                {
                  title: "Approvals",
                  items: [
                    {
                      label: "Provider",
                      value: (
                        <>
                          <p>{attendanceSheet?.submittedByProvider || "—"}</p>

                          {attendanceSheet?.submittedByProviderDate ? (
                            <Space>
                              <p className="text-xs opacity-50">
                                {attendanceSheet?.submittedByProviderDate
                                  ? dayjs(
                                      attendanceSheet?.submittedByProviderDate
                                    ).format("MMM DD, YYYY")
                                  : "—"}
                              </p>

                              <Checkmark />
                            </Space>
                          ) : null}
                        </>
                      ),
                    },
                    {
                      label: "Family",
                      value: (
                        <>
                          <p>{attendanceSheet?.submittedByFamily || "—"}</p>
                          {attendanceSheet?.status === "FAMILY_DENIED" ? (
                            <Space>
                              <p className="text-xs opacity-50">Rejected</p>

                              <X />
                            </Space>
                          ) : attendanceSheet?.submittedByFamilyDate ? (
                            <Space>
                              <p className="text-xs opacity-50">
                                {attendanceSheet?.submittedByFamilyDate
                                  ? dayjs(
                                      attendanceSheet?.submittedByFamilyDate
                                    ).format("MMM DD, YYYY")
                                  : "—"}
                              </p>

                              <Checkmark />
                            </Space>
                          ) : null}
                        </>
                      ),
                    },
                    ...(attendanceSheet?.declineReason
                      ? [
                          {
                            label: "Reason family declined",
                            value: attendanceSheet?.declineReason,
                          },
                        ]
                      : []),
                  ],
                },
              ]}
            />
          )}
        </div>
      </Card>

      {attendanceSheet?.submittedByProviderDate && (
        <Card
          title={isProviderView ? "Attendance" : "Provider Statement"}
          className="my-10"
        >
          <div className="flex justify-start gap-4 lg:gap-32 flex-col lg:flex-row mb-12 lg:mb-0 mt-2">
            {attendanceSheet?.submittedViaPaper !== true && (
              <>
                <List
                  items={[
                    {
                      title: "Details",
                      items: [
                        {
                          label: "Child attended during the billing period",
                          value: attendanceSheet?.attendedEntireMonth ? (
                            <Checkmark />
                          ) : (
                            <X />
                          ),
                        },
                        ...(attendanceSheet?.attendedEntireMonth
                          ? [
                              {
                                label:
                                  "Child’s first month of care or first approval month at this location",
                                value:
                                  attendanceSheet?.careStartMonth === true ? (
                                    <Checkmark />
                                  ) : (
                                    <X />
                                  ),
                              },
                              ...(attendanceSheet?.careStartMonth
                                ? [
                                    {
                                      label: "Care Start Date",
                                      value: attendanceSheet?.careStartDate
                                        ? dayjs(
                                            attendanceSheet?.careStartDate
                                          ).format("MMM DD, YYYY")
                                        : "—",
                                    },
                                  ]
                                : []),
                            ]
                          : []),
                        {
                          label: `Care terminated during the billing period`,
                          value: attendanceSheet?.terminationDate ? (
                            <Checkmark />
                          ) : (
                            <X />
                          ),
                        },
                        ...(attendanceSheet?.terminationDate
                          ? [
                              {
                                label: "Termination Date",
                                value: attendanceSheet?.terminationDate
                                  ? dayjs(
                                      attendanceSheet?.terminationDate
                                    ).format("MMM DD, YYYY")
                                  : "—",
                              },
                            ]
                          : []),
                      ],
                    },
                  ]}
                />

                {attendanceSheet?.attendedEntireMonth === true && (
                  <List
                    items={[
                      {
                        title: "Billing",
                        items: [
                          // Family charged the same amount as approved certificate
                          {
                            label:
                              "Family charged the same amount as approved certificate",
                            value: attendanceSheet?.chargedForMonth ? (
                              <Checkmark />
                            ) : (
                              <X />
                            ),
                          },
                          {
                            label:
                              "Monthly amount billed (if different from approved amount)",
                            value:
                              attendanceSheet?.amountBilled != null
                                ? `$${attendanceSheet.amountBilled.toFixed(2)}`
                                : "—",
                          },
                        ],
                      },
                    ]}
                  />
                )}
              </>
            )}
          </div>
        </Card>
      )}
    </>
  );
}
